import { useCallback } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { api, ApiError } from '@/lib/api';
import { queryKeys } from '@/lib/query-keys';
import type { Attachment, QueuedMessage } from '@runloop/reflex-contract';

/**
 * Thin client wrapper around the server-owned message queue (REST + WS).
 *
 * The queue lives on the server and is drained by the agent runner whenever
 * the agent transitions to an accepts-input state — so this hook never
 * tries to "send next" itself. Mutations write through the API and the
 * React Query cache; live updates come back over the WebSocket
 * (`agent:queue_updated`) and are written into the same cache by
 * `useAgentQueueSocket`.
 *
 * Returns the same shape the legacy in-memory hook used so existing
 * consumers (`AgentStreamDataContext`, queued-message renderer) don't need
 * to change.
 */
export function useMessageQueue(agentId: string | null) {
  const queryClient = useQueryClient();
  const enabled = Boolean(agentId);
  const queryKey = agentId ? [...queryKeys.agentQueue(agentId)] : ['agent-queue', 'disabled'];

  const { data: queue = [] } = useQuery<QueuedMessage[]>({
    queryKey,
    queryFn: () => api.getAgentQueue(agentId!),
    enabled,
    staleTime: Infinity,
    // Queue broadcasts are scoped to the mounted agent pane. If the runner
    // drains messages while that pane is unmounted, this cache misses the
    // broadcast and its otherwise-fresh data becomes a phantom queue. Keep
    // the cached value for an immediate render, but reconcile it with the
    // server every time the pane mounts again.
    refetchOnMount: 'always',
  });

  /**
   * Identity of the queue currently in the cache: which list it is, and when it
   * was last written.
   *
   * A mutation response only describes the queue as the server saw it while
   * answering. The server broadcasts the authoritative list via a Bus nudge
   * that each replica re-reads, so the broadcast can land after the HTTP
   * reply. Writing the response over a newer broadcast puts back rows the
   * runner has already drained — a message the agent is visibly working on,
   * sitting in the queue as though it were still to be sent. Mutations capture
   * this token and skip their local write when the cache has moved since; the
   * write stays as the fallback for a client whose socket is down, where no
   * broadcast arrives to move it.
   */
  const cacheToken = () => {
    const state = queryClient.getQueryState<QueuedMessage[]>(queryKey);
    return { data: state?.data, updatedAt: state?.dataUpdatedAt ?? 0 };
  };
  const cacheMovedSince = (token: ReturnType<typeof cacheToken>) => {
    const now = cacheToken();
    return now.data !== token.data || now.updatedAt !== token.updatedAt;
  };

  const enqueueMutation = useMutation({
    mutationFn: (input: { text: string; attachments?: Attachment[] }) =>
      api.enqueueAgentMessage(agentId!, input),
    onMutate: () => ({ token: cacheToken() }),
    onSuccess: (row, _vars, ctx) => {
      if (cacheMovedSince(ctx.token)) return;
      queryClient.setQueryData<QueuedMessage[]>(queryKey, (prev) => {
        if (!prev) return [row];
        if (prev.some((m) => m.id === row.id)) return prev;
        return [...prev, row];
      });
    },
    onError: (err: Error) => toast.error('Failed to queue message', { description: err.message }),
  });

  const updateMutation = useMutation({
    mutationFn: ({
      id,
      text,
      attachments,
    }: {
      id: string;
      text: string;
      attachments?: Attachment[];
    }) => api.updateQueuedMessage(agentId!, id, { text, attachments }),
    // Write the edit into the cache before the round-trip. Removing an
    // attachment sends the ones that survive, computed from this cache — so
    // without the optimistic write, a second removal issued before the first
    // response lands would be built from the stale list and resurrect the
    // attachment the first one dropped.
    onMutate: async ({ id, text, attachments }) => {
      await queryClient.cancelQueries({ queryKey });
      const snapshot = queryClient.getQueryData<QueuedMessage[]>(queryKey);
      queryClient.setQueryData<QueuedMessage[]>(queryKey, (prev) =>
        prev
          ? prev.map((m) =>
              m.id === id
                ? {
                    ...m,
                    text,
                    ...(attachments
                      ? { attachments: attachments.length ? attachments : null }
                      : {}),
                  }
                : m,
            )
          : prev,
      );
      return { snapshot };
    },
    onSuccess: (row) => {
      queryClient.setQueryData<QueuedMessage[]>(queryKey, (prev) =>
        prev ? prev.map((m) => (m.id === row.id ? row : m)) : prev,
      );
    },
    onError: (err: Error, _vars, ctx) => {
      if (ctx?.snapshot) queryClient.setQueryData(queryKey, ctx.snapshot);
      toast.error('Failed to update queued message', { description: err.message });
    },
  });

  const removeMutation = useMutation({
    mutationFn: (id: string) => api.deleteQueuedMessage(agentId!, id),
    onMutate: async (id) => {
      await queryClient.cancelQueries({ queryKey });
      const snapshot = queryClient.getQueryData<QueuedMessage[]>(queryKey);
      queryClient.setQueryData<QueuedMessage[]>(queryKey, (prev) =>
        prev ? prev.filter((m) => m.id !== id) : prev,
      );
      return { snapshot };
    },
    onError: (err: Error, id, ctx) => {
      // The runner drains the queue asynchronously, so a message can be
      // consumed (or already removed) between the moment it rendered and this
      // click. The server answers 404 in that case — the row is already gone,
      // which is exactly the state the user asked for. Treat it as a success:
      // keep it out of the cache instead of rolling back to a phantom row and
      // surfacing a confusing "Queued message not found" error.
      if (err instanceof ApiError && err.status === 404) {
        queryClient.setQueryData<QueuedMessage[]>(queryKey, (prev) =>
          prev ? prev.filter((m) => m.id !== id) : prev,
        );
        return;
      }
      if (ctx?.snapshot) queryClient.setQueryData(queryKey, ctx.snapshot);
      toast.error('Failed to remove queued message', { description: err.message });
    },
  });

  const reorderMutation = useMutation({
    mutationFn: (orderedIds: string[]) => api.reorderAgentQueue(agentId!, { orderedIds }),
    onMutate: async (orderedIds) => {
      await queryClient.cancelQueries({ queryKey });
      const snapshot = queryClient.getQueryData<QueuedMessage[]>(queryKey);
      queryClient.setQueryData<QueuedMessage[]>(queryKey, (prev) => {
        if (!prev) return prev;
        const byId = new Map(prev.map((m) => [m.id, m]));
        const next = orderedIds
          .map((id) => byId.get(id))
          .filter((m): m is QueuedMessage => Boolean(m));
        return next.length === prev.length ? next : prev;
      });
      // Read after the optimistic write so only a *later* broadcast counts.
      return { snapshot, token: cacheToken() };
    },
    onSuccess: (rows, _orderedIds, ctx) => {
      // The response carries the whole list, so applying it over a newer
      // broadcast would put a drained row back on screen.
      if (ctx && cacheMovedSince(ctx.token)) return;
      queryClient.setQueryData<QueuedMessage[]>(queryKey, rows);
    },
    onError: (err: Error, _orderedIds, ctx) => {
      if (ctx?.snapshot) queryClient.setQueryData(queryKey, ctx.snapshot);
      toast.error('Failed to reorder queued messages', { description: err.message });
    },
  });

  const enqueue = useCallback(
    (text: string, attachments?: Attachment[]) => {
      if (!agentId) return;
      enqueueMutation.mutate({
        text,
        attachments: attachments?.length ? attachments : undefined,
      });
    },
    [agentId, enqueueMutation],
  );

  /**
   * Edit a queued message. `attachments` replaces the stored list when
   * provided (that's how an attachment is removed); omit it to edit the text
   * alone and leave the attachments as they are.
   */
  const updateMessage = useCallback(
    (id: string, text: string, attachments?: Attachment[]) => {
      if (!agentId) return;
      updateMutation.mutate({ id, text, attachments });
    },
    [agentId, updateMutation],
  );

  const removeMessage = useCallback(
    (id: string) => {
      if (!agentId) return;
      removeMutation.mutate(id);
    },
    [agentId, removeMutation],
  );

  const reorder = useCallback(
    (orderedIds: string[]) => {
      if (!agentId) return;
      reorderMutation.mutate(orderedIds);
    },
    [agentId, reorderMutation],
  );

  return { queue, enqueue, updateMessage, removeMessage, reorder };
}
